import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { T } from "services/lang/LanguageService";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { DatasetStringValueMapperDTO } from "../../../charts/interfaces";

interface CreateEditDatasetMapperProps {
  mapperData?: DatasetStringValueMapperDTO;
  onSave: (data: DatasetStringValueMapperDTO) => void;
  onCancel: () => void;
}
const CreateEditDatasetMapper = ({
  mapperData,
  onSave,
  onCancel,
}: CreateEditDatasetMapperProps) => {
  const [datasetMapperTitle, setDatasetMapperTitle] = useState<string>("");
  const [global, setGlobal] = useState<boolean>(false);
  const [mappings, setMappings] = useState<{ key: string; value: string }[]>([
    { key: "", value: "" },
  ]);
  const [newMapper, setNewMapper] = useState<DatasetStringValueMapperDTO>();

  useEffect(() => {
    if (!mapperData) {
      createDatasetMapperHandler();
    }
  }, [global, mappings, datasetMapperTitle]);

  useEffect(() => {
    if (mapperData) {
      setDatasetMapperTitle(mapperData.title);
      setGlobal(mapperData.global);
      setMappings(
        Object.entries(mapperData.mappings).map(([key, value]) => ({
          key,
          value,
        }))
      );
    }
  }, [mapperData]);

  const createDatasetMapperHandler = () => {
    if (!mapperData) {
      const data: DatasetStringValueMapperDTO = {
        global,
        title: datasetMapperTitle,
        mappings: mappings.reduce((acc: any, curr) => {
          if (curr.key && curr.value) {
            acc[curr.key] = curr.value;
          }
          return acc;
        }, {}),
      };
      setNewMapper(data);
    }
  };

  const handleMappingChange = (index: number, key: string, value: string) => {
    const newMappings = [...mappings];
    newMappings[index] = { key, value };
    setMappings(newMappings);
  };

  const addMapping = () => {
    setMappings([...mappings, { key: "", value: "" }]);
  };

  const removeMapping = (index: number) => {
    const newMappings = [...mappings];
    newMappings.splice(index, 1);
    setMappings(newMappings);
  };

  const handleSave = () => {
    if (!mapperData) {
      if (newMapper?.title.trim() === "") {
        easasAlert({ summary: T("alert.title-missing"), detail: T("alert.please-enter-a-title"), severity: "error" });
        return;
      }
      if (Object.keys(newMapper?.mappings ?? {}).length === 0) {
        easasAlert({
          summary: T("alert.mappings-missing"),
          detail: T("alert.please-enter-at-least-one-mapping"),
          severity: "error",
        });
        return;
      }
      if (newMapper) {
        onSave(newMapper);
      }
    } else {
      if (datasetMapperTitle.trim() === "") {
        easasAlert({ summary: T("alert.title-missing"), detail: T("alert.please-enter-a-title"), severity: "error" });
        return;
      }
      if (mapperData) {
        const data: DatasetStringValueMapperDTO = {
          id: mapperData.id,
          global,
          company: mapperData.company,
          title: datasetMapperTitle,
          mappings: mappings.reduce((acc: any, { key, value }) => {
            if (key && value) {
              acc[key] = value;
            }
            return acc;
          }, {}),
        };
        if (Object.keys(data?.mappings ?? {}).length === 0) {
          easasAlert({
            summary: T("alert.mappings-missing"),
            detail: T("alert.please-enter-at-least-one-mapping"),
            severity: "error",
          });
          return;
        }
        onSave(data);
      }
    }
  };

  return (
      <>
        <div className="flex col-12 align-items-center">
          <p className="col-4 mb-0">{T("label.mapper.dataset-mapper-title")}</p>
          <InputText
            className="w-full"
            value={datasetMapperTitle}
            onChange={(e) => setDatasetMapperTitle(e.target.value)}
            placeholder={T("label.mapper.new-dataset-mapper-title")}
          />
        </div>
        <PermissionGuard permissions="ADMIN">
          <div className="flex col-12 align-items-center">
            <p className="col-4 mb-0">{T("word.global")}</p>
            <InputSwitch
              checked={global}
              onChange={(e) => setGlobal(e.value)}
            />
          </div>
        </PermissionGuard>
        <h6>{T("word.mappings")}</h6>
        {mappings.map((mapping, index) => (
          <div key={index} className="flex col-12 align-items-center gap-2">
            <InputText
              className="w-full"
              aria-placeholder={T("word.index")}
              value={mapping.key}
              onChange={(e) =>
                handleMappingChange(index, e.target.value, mapping.value)
              }
              placeholder={T("word.key")}
              keyfilter="int"
            />
            <InputText
              className="w-full"
              aria-placeholder={T("word.meaning")}
              value={mapping.value}
              onChange={(e) =>
                handleMappingChange(index, mapping.key, e.target.value)
              }
              placeholder={T("word.value")}
            />
            <Button
              type="button"
              icon="pi pi-trash"
              className="const-del-btn  p-button-rounded p-button-text p-button-danger"
              onClick={() => removeMapping(index)}
            />
          </div>
        ))}
        <div className="col-12">
          <Button
            className="p-button-outlined p-button-secondary"
            label={T("action.add-mapping")}
            onClick={addMapping}
          />
        </div>
        <div className="flex col-12 gap-2 justify-content-end">
          <Button
            className="p-button-danger"
            label={T("word.cancel")}
            onClick={onCancel}
          />
          <Button
            className="p-button-success"
            label={!mapperData ? T("action.create"): T("action.save")}
            onClick={handleSave}
          />          
        </div>
      </>
  );
};

export default CreateEditDatasetMapper;
